require("./threepl-supply-account-select-panel.scss");

import {ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Subject, EMPTY} from "rxjs";
import {catchError, exhaustMap, switchMap, tap} from "rxjs/operators";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {AccountModel} from "../../../../account/angular/models/account.model";
import {ThreePLSupplyAccountsHttpService} from "../../services/threepl-supply-accounts-http.service";
import {SUPPLY_ACCOUNTS_PAGE_SIZE, SUPPLY_ACCOUNTS_SEARCH_DEBOUNCE_MS} from "../../constants/threepl-train.constant";

export interface SupplyAccountPanelData {
  selectedId?: string;
  type: string;
  placeholder?: string;
}

export type SupplyAccountPanelResult = {kind: "select"; account: AccountModel} | {kind: "reset"};

@Component({
  selector: "app-threepl-supply-account-select-panel",
  templateUrl: "./threepl-supply-account-select-panel.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeplSupplyAccountSelectPanelComponent {
  readonly data = inject<SupplyAccountPanelData>(PANEL_DATA);
  readonly onClose = output<SupplyAccountPanelResult>();

  readonly accounts = signal<AccountModel[]>([]);
  readonly loading = signal(false);
  readonly debounceMs = SUPPLY_ACCOUNTS_SEARCH_DEBOUNCE_MS;

  private readonly http = inject(ThreePLSupplyAccountsHttpService);
  private readonly query$ = new Subject<string | undefined>();
  private readonly next$ = new Subject<void>();
  private query: string | undefined;
  private cursor = "";
  private finished = false;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.query$.pipe(
      tap((query) => {
        this.query = query;
        this.cursor = "";
        this.finished = false;
        this.accounts.set([]);
        this.loading.set(true);
      }),
      switchMap((query) => this.fetch(query)),
      takeUntilDestroyed(destroyRef),
    ).subscribe();
    this.next$.pipe(
      exhaustMap(() => {
        if (this.finished || this.loading()) {
          return EMPTY;
        }
        this.loading.set(true);
        return this.fetch(this.query);
      }),
      takeUntilDestroyed(destroyRef),
    ).subscribe();
    this.query$.next(undefined);
  }

  onSearch(query: string): void {
    this.query$.next(query || undefined);
  }

  onFetchNext(): void {
    this.next$.next();
  }

  onSelect(accountId: string | number | null): void {
    const account = this.accounts().find((a) => a.id === accountId);
    if (account) {
      this.onClose.emit({kind: "select", account});
    }
  }

  onReset(): void {
    this.onClose.emit({kind: "reset"});
  }

  private fetch(query: string | undefined) {
    const requested = query;
    return this.http.list({type: this.data.type, query, limit: SUPPLY_ACCOUNTS_PAGE_SIZE, cursor: this.cursor || undefined}).pipe(
      tap((page) => {
        if (requested !== this.query) {
          return;
        }
        this.accounts.update((accounts) => [...accounts, ...page.list]);
        this.cursor = page.cursor;
        this.finished = !page.cursor || page.list.length < SUPPLY_ACCOUNTS_PAGE_SIZE;
        this.loading.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        return EMPTY;
      }),
    );
  }
}
